// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { get, post } from '@tls/auth';
import { DataLoggerParameter, QCProgram } from '@tls/sw91_ui_templates/lib/models/project-models';
import { useCallback } from 'react';

interface ComputeQCPResponse {
  algoResultImages: {
    base64JpegImage: string;
    imageName: string;
  }[];
  segmentationResultImage: {
    base64JpegImage: string;
    imageName: string;
  };
  dataLoggerOutput: Record<string, DataLoggerParameter>;
}

const useQCPApi = () => {
  const fetchQCPProject = useCallback((projectId: string) => {
    return get(`projects/${projectId}/qcprogram`).then(response => {
      if (response.status > 199 && response.status < 300) {
        return response.data as QCProgram;
      }
      throw new Error('Failed to fetch QCP project');
    });
  }, []);

  const fetchAvailableAlgorithms = useCallback(() => {
    return get('QcProgramExecution/plugins/builtin')
      .then(response => {
        if (response.status > 199 && response.status < 300) {
          return response.data;
        }
        throw new Error('Failed to fetch available algorithms');
      })
      .then(data => {
        return data;
      });
  }, []);

  const putQCPProject = useCallback((program: QCProgram) => {
    const body = {
      qcpJson: JSON.stringify(program),
      qcpMetaInfo: {
        programId: program.info.program_id,
        programName: program.info.program_name,
      },
    };

    return post(`projects/${program.info.project_id}/qcprogram`, body).then(response => {
      if (response.status > 199 && response.status < 300) {
        return response.data;
      }
      throw new Error('Failed to update QCP project');
    });
  }, []);

  const computeQCP = useCallback((projectId: string, imageMd5: string, strategyVersion: string, program: QCProgram) => {
    const body = {
      qcpJson: JSON.stringify(program),
      algorithmName: 'all',
    };
    return post(`projects/${projectId}/computeqcp/${strategyVersion}/${imageMd5}`, body).then(response => {
      console.log('computeQCP response', response);
      if (response.status > 199 && response.status < 300) {
        return response.data as ComputeQCPResponse;
      }
      throw new Error('Failed to compute QCP');
    });
  }, []);

  return {
    fetchQCPProject,
    fetchAvailableAlgorithms,
    postQCPProject: putQCPProject,
    computeQCP,
  };
};

export default useQCPApi;
